/**
 * User Profile Context
 * 
 * Centralized user profile management that caches profile data in sessionStorage
 * to avoid repeated API calls throughout the application.
 * 
 * Features:
 * - Automatically fetches profile when user logs in
 * - Caches profile in sessionStorage for fast access
 * - Clears profile on logout or session close
 * - Provides refresh method to update profile data
 * 
 * Usage:
 * ```tsx
 * import { useUserProfile } from '@/hooks/useUserProfile';
 * 
 * function MyComponent() {
 *   const { userProfile, loading, error, refreshProfile } = useUserProfile();
 *   
 *   if (loading) return <div>Loading...</div>;
 *   if (error) return <div>Error: {error}</div>;
 *   
 *   return (
 *     <div>
 *       <h1>Welcome {userProfile?.name}</h1>
 *       <p>Slug: {userProfile?.slug}</p>
 *       <button onClick={refreshProfile}>Refresh Profile</button>
 *     </div>
 *   );
 * }
 * ```
 * 
 * Notes:
 * - Profile is automatically cleared when user logs out via signOut()
 * - Profile is cached in sessionStorage (cleared when browser tab closes)
 * - Use refreshProfile() after updating profile data to sync the cache
 */
import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { api } from '@/lib/api';
import { useAuth } from './useAuth';

// Deep sanitize profile data using structuredClone (faster and safer on mobile)
function deepSanitizeProfile(data: any): any {
  if (!data) return data;
  
  try {
    // Use structuredClone for deep copy (faster than JSON parse/stringify on mobile)
    if (typeof structuredClone !== 'undefined') {
      const sanitized = structuredClone(data);
      console.log('🧹 [Profile] Sanitized profile data (structuredClone)');
      return sanitized;
    }
    
    // Fallback to JSON if structuredClone not available
    const jsonString = JSON.stringify(data);
    const sanitized = JSON.parse(jsonString);
    console.log('🧹 [Profile] Sanitized profile data (JSON)');
    return sanitized;
  } catch (error) {
    console.error('❌ [Profile] Error sanitizing profile:', error);
    // If sanitization fails, return a minimal safe object
    return {
      success: true,
      user: data.user || {},
      profile: {
        _id: data.profile?._id,
        email: data.profile?.email,
        name: data.profile?.name,
        cardId: data.profile?.cardId,
        slug: data.profile?.slug
      }
    };
  }
}

interface UserProfile {
  _id?: string;
  userId?: string;
  email?: string;
  name?: string;
  username?: string;
  slug?: string;
  cardId?: string;
  bio?: string;
  title?: string;
  company?: string;
  location?: string;
  website?: string;
  phone?: string;
  socialLinks?: any;
  profilePicture?: string;
  accountType?: string;
  qrCodes?: any[];
  customFields?: any[];
  // Add any other fields from your Profile model
  [key: string]: any;
}

interface UserProfileContextType {
  userProfile: UserProfile | null;
  loading: boolean;
  error: string | null;
  refreshProfile: () => Promise<void>;
  clearProfile: () => void;
}

const UserProfileContext = createContext<UserProfileContextType | undefined>(undefined);

export const UserProfileProvider = ({ children }: { children: ReactNode }) => {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const { user, session, loading: authLoading } = useAuth();
  
  // Guards to prevent infinite fetch loops
  const profileLoaded = useRef(false);
  const fetchingRef = useRef(false);

  // Listen for admin-triggered profile cache invalidation events
  useEffect(() => {
    const handleProfileCacheCleared = () => {
      console.log('🔔 [Profile] Admin profile update detected — refreshing profile');
      sessionStorage.removeItem('user_profile');
      sessionStorage.removeItem('profile_cache_version');
      profileLoaded.current = false;
      fetchingRef.current = false;
      // Bump the refresh key to re-trigger the main profile-loading effect
      setRefreshKey(k => k + 1);
    };
    window.addEventListener('profile-cache-cleared', handleProfileCacheCleared);
    return () => window.removeEventListener('profile-cache-cleared', handleProfileCacheCleared);
  }, []);

  // Fetch user profile when user is authenticated
  useEffect(() => {
    // Don't do anything while auth is still loading
    if (authLoading) {
      return;
    }

    if (user && session) {
      // ✅ FIX: Prevent infinite loop - only load profile once per session
      if (profileLoaded.current) {
        console.log('⏭️ [Profile] Profile already loaded, skipping');
        return;
      }
      
      profileLoaded.current = true;
      
      // Check profile cache version - force clear if outdated
      const PROFILE_CACHE_VERSION = '6.1'; // 6.1 - qrBehavior added to user-profile endpoint
      const cachedVersion = sessionStorage.getItem('profile_cache_version');
      
      if (cachedVersion !== PROFILE_CACHE_VERSION) {
        console.log('🔄 [Profile] Clearing old profile cache (version mismatch)');
        console.log(`   Old: ${cachedVersion || 'none'}, New: ${PROFILE_CACHE_VERSION}`);
        sessionStorage.removeItem('user_profile');
        sessionStorage.setItem('profile_cache_version', PROFILE_CACHE_VERSION);
      }
      
      // Check if profile exists in sessionStorage first
      const cachedProfile = sessionStorage.getItem('user_profile');
      if (cachedProfile) {
        try {
          console.log('📦 [Profile] Loading profile from sessionStorage cache');
          const parsedProfile = JSON.parse(cachedProfile);
          
          // Sanitize cached profile before setting state (prevent old circular refs)
          const sanitizedProfile = deepSanitizeProfile(parsedProfile);
          setUserProfile(sanitizedProfile);
          setLoading(false);
          console.log('✅ [Profile] Loaded cached profile');

          // Always revalidate from server in background to catch admin/cascade changes
          fetchProfile();
        } catch (error) {
          console.error('❌ [Profile] Failed to parse cached profile:', error);
          sessionStorage.removeItem('user_profile');
          // Fetch profile if cache is corrupted
          fetchProfile();
        }
      } else {
        // Fetch profile if not cached
        console.log('🔍 [Profile] No cache found, fetching from server');
        fetchProfile();
      }
    } else {
      // Clear profile when user is not authenticated
      clearProfile();
      profileLoaded.current = false;
      setLoading(false);
    }
  }, [user, session, authLoading, refreshKey]);

  const fetchProfile = async () => {
    // ✅ FIX: Prevent double fetch calls
    if (fetchingRef.current) {
      console.log('⏸️ [Profile] Already fetching, skipping duplicate call');
      return;
    }
    
    fetchingRef.current = true;
    
    try {
      setLoading(true);
      setError(null);
      
      console.log('🌐 [Profile] Fetching from API: /api/auth/user-profile');
      const response = await api.getUserProfile();
      console.log('🌐 [Profile] API response received:', { 
        hasResponse: !!response,
        keys: response ? Object.keys(response) : []
      });
      
      if (response) {
        // Check if server sent cache version header (means data was just updated)
        const cacheVersion = response.cacheVersion || response.profile?.updatedAt;
        const cachedVersion = sessionStorage.getItem('profile_cache_timestamp');
        
        // If server version is newer than cached, clear cache
        if (cacheVersion && cachedVersion && cacheVersion !== cachedVersion) {
          console.log('🔄 [Profile] Server data is newer, clearing stale cache');
          sessionStorage.removeItem('user_profile');
        }
        
        // Sanitize response to remove any circular references before setting state
        console.log('🧹 [Profile] Sanitizing API response...');
        const sanitizedResponse = deepSanitizeProfile(response);
        
        console.log('✅ [Profile] Setting sanitized profile state');
        // ✅ FIX: Prevent unnecessary re-renders by comparing prev/next state
        setUserProfile(prev => {
          // If profile hasn't changed, return previous reference (prevents re-render)
          if (prev && JSON.stringify(prev) === JSON.stringify(sanitizedResponse)) {
            console.log('⏭️ [Profile] Profile unchanged, skipping update');
            return prev;
          }
          return sanitizedResponse;
        });
        
        // Store in sessionStorage for quick access (with circular reference protection)
        try {
          const PROFILE_CACHE_VERSION = '6.0';
          console.log('💾 [Profile] Caching sanitized profile in sessionStorage');
          // Use regular JSON.stringify since we already sanitized
          sessionStorage.setItem('user_profile', JSON.stringify(sanitizedResponse));
          sessionStorage.setItem('profile_cache_version', PROFILE_CACHE_VERSION);
          // Store timestamp for cache staleness detection
          if (cacheVersion) {
            sessionStorage.setItem('profile_cache_timestamp', cacheVersion.toString());
          }
          console.log('✅ [Profile] Profile cached successfully');
        } catch (storageError) {
          console.warn('⚠️ [Profile] Failed to cache profile in sessionStorage:', storageError);
          // Continue without caching - not critical
        }
      }
    } catch (err: any) {
      console.error('❌ [Profile] Failed to fetch user profile:', err);
      setError(err.message || 'Failed to load profile');
    } finally {
      setLoading(false);
      fetchingRef.current = false;
    }
  };

  const refreshProfile = async () => {
    // Force refresh by removing cache and resetting guards
    console.log('🔄 [Profile] Forcing profile refresh');
    sessionStorage.removeItem('user_profile');
    profileLoaded.current = false; // Reset guard to allow re-fetch
    fetchingRef.current = false; // Reset fetching guard to prevent skip
    await fetchProfile();
  };

  const clearProfile = () => {
    console.log('🧹 [Profile] Clearing profile cache');
    setUserProfile(null);
    setError(null);
    sessionStorage.removeItem('user_profile');
    sessionStorage.removeItem('profile_cache_version');
  };

  return (
    <UserProfileContext.Provider 
      value={{ 
        userProfile, 
        loading, 
        error, 
        refreshProfile, 
        clearProfile 
      }}
    >
      {children}
    </UserProfileContext.Provider>
  );
};

export const useUserProfile = () => {
  const context = useContext(UserProfileContext);
  if (context === undefined) {
    throw new Error('useUserProfile must be used within a UserProfileProvider');
  }
  return context;
};
